import type { BooleanInput, NumberInput } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, numberAttribute } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleCheck, lucideInfo, lucideLoader2, lucideOctagonX, lucideTriangleAlert } from '@ng-icons/lucide';
import { BrnSonnerImports, toast, toastState, type ToasterProps } from '@spartan-ng/brain/sonner';
import { hlm } from '@spartan-ng/helm/utils';
import type { ClassValue } from 'clsx';

/**
 * Sonner's rich colours, re-pointed at the design tokens (docs/design-tokens.md).
 *
 * Out of the box they are fixed `hsl` values keyed on the toaster's `theme`, which is `light` here
 * whatever the app is in — so they ignored dark mode and every tenant palette. The tokens already
 * flip under `.dark`, so the light and dark variables get the same expression.
 *
 * The tint is the `bg-{token}/10` + `border-{token}/40` callout, mixed into `--popover` rather than
 * transparency because a toast floats over content. The text is not the bare token: on that tint it
 * fails AA in light mode for all four (warning 2.76:1). 65% token / 35% foreground keeps the hue and
 * measures at least 5.9:1 in both themes.
 */
const TOAST_TYPE_COLOURS: Record<string, string> = Object.fromEntries(
	(
		[
			['error', 'destructive'],
			['success', 'success'],
			['warning', 'warning'],
			['info', 'info'],
		] as const
	).flatMap(([type, token]) =>
		['', 'dark-'].flatMap((theme) => [
			[`--brn-sonner-toast-${theme}${type}-background`, `color-mix(in oklch, var(--${token}) 10%, var(--popover))`],
			[`--brn-sonner-toast-${theme}${type}-border`, `color-mix(in oklch, var(--${token}) 40%, var(--popover))`],
			[`--brn-sonner-toast-${theme}${type}-color`, `color-mix(in oklch, var(--${token}) 65%, var(--popover-foreground))`],
		]),
	),
);

@Component({
	selector: 'hlm-toaster',
	imports: [BrnSonnerImports, NgIcon],
	providers: [provideIcons({ lucideCircleCheck, lucideInfo, lucideTriangleAlert, lucideOctagonX, lucideLoader2 })],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<brn-sonner-toaster
			[class]="_computedClass()"
			[invert]="invert()"
			[theme]="theme()"
			[position]="position()"
			[hotKey]="hotKey()"
			[richColors]="richColors()"
			[expand]="expand()"
			[duration]="duration()"
			[visibleToasts]="visibleToasts()"
			[closeButton]="closeButton()"
			[toastOptions]="_computedToastOptions()"
			[offset]="offset()"
			[style]="_computedStyle()"
		>
			<ng-template #loadingIcon>
				<ng-icon name="lucideLoader2" class="overflow-visible! text-base [&>svg]:motion-safe:animate-spin" />
			</ng-template>
			<ng-template #successIcon>
				<ng-icon name="lucideCircleCheck" class="overflow-visible! text-base" />
			</ng-template>
			<ng-template #errorIcon>
				<ng-icon name="lucideOctagonX" class="overflow-visible! text-base" />
			</ng-template>
			<ng-template #infoIcon>
				<ng-icon name="lucideInfo" class="overflow-visible! text-base" />
			</ng-template>
			<ng-template #warningIcon>
				<ng-icon name="lucideTriangleAlert" class="overflow-visible! text-base" />
			</ng-template>
		</brn-sonner-toaster>
		@if (_showDismissAll()) {
			<!-- dir="ltr": sonner places the stack with physical left/right, so start/end must resolve the same way -->
			<button type="button" dir="ltr" [class]="_dismissAllClass()" (click)="dismissAll()">
				Clear all ({{ _toastCount() }})
				<span class="sr-only">notifications</span>
			</button>
		}
	`,
})
export class HlmToaster {
	public readonly invert = input<ToasterProps['invert'], BooleanInput>(false, {
		transform: booleanAttribute,
	});
	public readonly theme = input<ToasterProps['theme']>('light');
	public readonly position = input<ToasterProps['position']>('bottom-right');
	public readonly hotKey = input<ToasterProps['hotkey']>(['altKey', 'KeyT']);
	public readonly richColors = input<ToasterProps['richColors'], BooleanInput>(true, {
		transform: booleanAttribute,
	});
	public readonly expand = input<ToasterProps['expand'], BooleanInput>(false, {
		transform: booleanAttribute,
	});
	public readonly duration = input<ToasterProps['duration'], NumberInput>(4000, {
		transform: numberAttribute,
	});
	public readonly visibleToasts = input<ToasterProps['visibleToasts'], NumberInput>(3, {
		transform: numberAttribute,
	});
	/**
	 * On by default: with it off, a toast could only be swiped away, and the sticky ones
	 * (`duration: Infinity`) stayed until something else retired them unless their call site
	 * remembered to opt in.
	 */
	public readonly closeButton = input<ToasterProps['closeButton'], BooleanInput>(true, {
		transform: booleanAttribute,
	});
	/** How many toasts must be on screen before the "Clear all" button appears. */
	public readonly dismissAllThreshold = input<number, NumberInput>(2, {
		transform: numberAttribute,
	});

	/** The toasts "Clear all" may close: a `dismissible: false` toast or a call still loading stays. */
	private readonly _dismissibleToasts = computed(() =>
		toastState.toasts().filter((shown) => shown.dismissible !== false && shown.type !== 'loading'),
	);
	protected readonly _toastCount = computed(() => this._dismissibleToasts().length);
	protected readonly _showDismissAll = computed(() => {
		const threshold = this.dismissAllThreshold();
		return this._toastCount() >= (Number.isFinite(threshold) && threshold > 1 ? threshold : 2);
	});

	/** Every dismissible toast, at once — including sticky ones nothing else would retire. */
	public dismissAll(): void {
		for (const shown of this._dismissibleToasts()) {
			toast.dismiss(shown.id);
		}
	}
	public readonly toastOptions = input<ToasterProps['toastOptions']>({});

	protected readonly _computedToastOptions = computed(() => {
		const options = this.toastOptions();
		return {
			...options,
			classes: {
				...options?.classes,
				toast: hlm('rounded-2xl!', options?.classes?.toast),
			},
		};
	});
	public readonly offset = input<ToasterProps['offset']>(null);
	public readonly userClass = input<ClassValue>('', { alias: 'class' });
	public readonly userStyle = input<Record<string, string>>(
		{
			'--normal-bg': 'var(--popover)',
			'--normal-text': 'var(--popover-foreground)',
			'--normal-border': 'var(--border)',
			'--border-radius': 'var(--radius)',
		},
		{ alias: 'style' },
	);

	protected readonly _computedStyle = computed(() => ({ ...TOAST_TYPE_COLOURS, ...this.userStyle() }));

	/*
	 * The "Clear all" button sits in the gap between the stack and the viewport edge. Sonner leaves
	 * 32px there on desktop but only 20px at ≤600px, too little for the button, so on narrow screens
	 * the stack is lifted to the desktop gap.
	 */
	private readonly _yEdge = computed(() => (this.position()?.startsWith('top') ? 'top' : 'bottom'));

	protected readonly _computedClass = computed(() =>
		hlm(
			'toaster group',
			this._showDismissAll() && (this._yEdge() === 'top' ? 'max-[600px]:top-8!' : 'max-[600px]:bottom-8!'),
			this.userClass(),
		),
	);

	protected readonly _dismissAllClass = computed(() => {
		const position = this.position() ?? 'bottom-right';
		return hlm(
			// Above sonner's own 999999999, which already outranks the CDK overlay container.
			'fixed z-[1000000000] inline-flex h-6 items-center rounded-full border border-border bg-popover px-3 text-xs font-medium text-popover-foreground shadow-md',
			'hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
			this._yEdge() === 'top' ? 'top-1' : 'bottom-1',
			position.endsWith('left') && 'start-8 max-[600px]:start-4',
			position.endsWith('right') && 'end-8 max-[600px]:end-4',
			position.endsWith('center') && 'start-1/2 -translate-x-1/2',
		);
	});
}
